import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

import { SearchResultItem } from '@models/SearchResults';
import { Country } from '@models/CountriesAndLanguages';
import { SimpleTitle, TTitleContext } from '@models/Title';
import { useAppDispatch } from '@store/hooks';
import { titlesActions } from '@store/titles/slice';

import MetadataContext, { GetTitleLinkPropsFn, MetadataContextValues } from './context';

const MetadataContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const query = queryString.parse(search);

  const masterId = query.masterId?.toString();
  const providerIdentifier = query.providerIdentifier?.toString();
  const playbackOptionId = query.playbackOptionId?.toString();
  const platformTitleId = query.platformTitleId?.toString();
  const country: Country | undefined =
    query.country === 'no' || query.country === 'dk' ? query.country : undefined;

  const id = masterId || platformTitleId;
  const hasId = Boolean(id);

  const getInitTitlePath = (): TTitleContext[] => {
    if (masterId) return [{ country, masterId, playbackOptionId, providerIdentifier }];
    if (platformTitleId) return [{ country, platformTitleId, providerIdentifier }];
    return [];
  };

  const [highlights, setHighlights] = useState<Record<string, Record<string, string[]>>>({});
  const [titlePath, setTitlePath] = useState<TTitleContext[]>(getInitTitlePath());
  const [viewTitleAsSubscriber, setViewTitleAsSubscriber] = useState(false);

  const dispatch = useAppDispatch();

  const handleAddSimpleTitles = (titles: SimpleTitle[]) =>
    dispatch(titlesActions.addSimpleTitles({ titles }));

  const addHighlights = (masterId: string, highlights: Record<string, string[]>) =>
    setHighlights(prev => ({ ...prev, [masterId]: highlights }));

  const setSearchResults = (searchResults: SearchResultItem<SimpleTitle>[]) => {
    handleAddSimpleTitles(searchResults.map(sr => sr.value));

    const newHightlights = searchResults.reduce(
      (h, item) => ({ ...h, [item.value.masterId]: item.highlights }),
      {}
    );

    setHighlights(() => ({ ...newHightlights }));
  };

  const updateTitlePath = (titleContext: TTitleContext, initViewTitleAsSubscriber?: boolean) => {
    if (initViewTitleAsSubscriber != null && viewTitleAsSubscriber !== initViewTitleAsSubscriber)
      setViewTitleAsSubscriber(initViewTitleAsSubscriber);

    let isSameTitle = false;

    if (titlePath.length > 0) {
      const prevElem = titlePath.at(-1);

      if (
        (prevElem?.masterId && prevElem?.masterId === titleContext.masterId) ||
        (prevElem?.platformTitleId && prevElem?.platformTitleId === titleContext.platformTitleId)
      )
        isSameTitle = true;
    }

    if (!isSameTitle) setTitlePath(prev => [...prev, titleContext]);
    else
      setTitlePath(prev => {
        prev.splice(-1, 1, titleContext);
        return prev;
      });
  };

  const selectTitle = (titleContext: TTitleContext, initViewTitleAsSubscriber?: boolean) => {
    updateTitlePath(titleContext, initViewTitleAsSubscriber);

    navigate(
      { pathname, search: queryString.stringify({ ...query, ...titleContext }) },
      { preventScrollReset: true, replace: true }
    );
  };

  const getTitleLinkProps: GetTitleLinkPropsFn = (titleContext, initViewTitleAsSubscriber) => ({
    to: {
      pathname,
      search: queryString.stringify({ ...query, ...titleContext }),
    },
    preventScrollReset: true,
    replace: true,
    onClick: e => {
      if (!e.ctrlKey) updateTitlePath(titleContext, initViewTitleAsSubscriber);
    },
  });

  useEffect(() => {
    if (!hasId && viewTitleAsSubscriber) setViewTitleAsSubscriber(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterId, platformTitleId]);

  const closeDetailPanel = () => {
    let parentTitle: TTitleContext | undefined;

    setTitlePath(prev => {
      prev.pop();
      parentTitle = prev.at(-1);
      return prev;
    });

    let tmpSearch = { ...query };

    if (parentTitle) tmpSearch = { ...query, ...parentTitle };
    else {
      delete tmpSearch.masterId;
      delete tmpSearch.platformTitleId;
      delete tmpSearch.playbackOptionId;
      delete tmpSearch.providerIdentifier;
      if (pathname.startsWith('/ingest/possible-merges')) delete tmpSearch.country;
    }

    navigate(
      { pathname, search: queryString.stringify(tmpSearch) },
      { preventScrollReset: true, replace: true }
    );
  };

  const selectedHighlights = masterId ? highlights[masterId] || {} : {};

  const values: MetadataContextValues = {
    addHighlights,
    addSimpleTitles: handleAddSimpleTitles,
    closeDetailPanel,
    getTitleLinkProps,
    highlights,
    selectedHighlights,
    selectTitle,
    setSearchResults,
    setViewTitleAsSubscriber,
    titlePath,
    viewTitleAsSubscriber,
  };

  return <MetadataContext.Provider value={values}>{children}</MetadataContext.Provider>;
};

export default MetadataContextProvider;
